import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import styled from '@emotion/styled'
import { Button } from '@mobi/component-library/Common/V2'
import { colors, font, radius, spacing } from '@mobi/component-library/Theme/Common'
import { startCognitoAttributeVerification } from '../Hooks/useMfaVerification'
import { MFAMethod, MfaError } from '@mobi/api-types/src/mfa'

import { mapMFAMethodToAttributeName } from '../Helpers/utils'
import { getMFAMethodLabel, handleMfaError, trackMfaEvent } from '../Helpers/helpers'
import { MFASelectionItem } from './MFASelectionItem'
import { useMfaUpdateAuthMutation } from '../Hooks/useMfaUpdateAuthMutation'
import { CognitoUnauthorizedError } from '../Hooks/useCognitoUserIsAuthenticated'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'
import { lessThan } from '@mobi/component-library/Utils/media'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { useFeature } from '@core/Utils/hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { usePasswordVerificationTray } from '../Hooks/usePasswordVerificationTray'
import { PasswordVerificationTray } from './PasswordVerificationTray'

export const MFASelectionScreen: React.FC<{ accountNumber: number }> = ({ accountNumber }) => {
  const navigate = useNavigate()
  const isOtpContactDetailsEditEnabled = useFeature(FeatureFlags.OTP_CONTACT_DETAILS_EDIT.key)

  const { mutateAsync: updateMfaPreference } = useMfaUpdateAuthMutation()
  const { isPasswordTrayOpen, closeVerifyPasswordTray, handleCognitoError, handleVerify } =
    usePasswordVerificationTray()

  const [isLoading, setIsLoading] = useState(false)
  const {
    data: cognitoUser,
    isLoading: cognitoUserLoading,
    isError: isCognitoUserError,
  } = useCognitoUser(accountNumber)
  const activeMethod = cognitoUser?.preferredFactor || 'None'

  function navigateToEditContact() {
    navigate('/account/contactdetails')
  }

  function navigateToVerifyCode(preferredFactor: MFAMethod) {
    navigate(`/account/settings/mfa/verify-code/${preferredFactor}`)
  }

  const onVerify = async (mfaMethod: MFAMethod) => {
    setIsLoading(true)
    try {
      await startCognitoAttributeVerification({
        attributeName: mapMFAMethodToAttributeName(mfaMethod),
      })
      navigateToVerifyCode(mfaMethod)
    } catch (error) {
      if (!handleCognitoError(error, () => onVerify(mfaMethod))) {
        handleMfaError(error as MfaError, 'verification')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onToggle = async (mfaMethod: MFAMethod) => {
    setIsLoading(true)
    const newMethod = activeMethod === mfaMethod ? 'None' : mfaMethod
    try {
      await updateMfaPreference({ preferredFactor: newMethod })
      if (newMethod == 'None') {
        addToast({
          id: 'mfa-success',
          isDismissable: true,
          isFullWidth: true,
          title: 'MFA disabled for this account',
          icon: 'success',
        })
      } else {
        addToast({
          id: 'mfa-success',
          isDismissable: true,
          isFullWidth: true,
          title: `${getMFAMethodLabel(newMethod)} is now enabled for MFA`,
          icon: 'success',
        })
      }

      trackMfaEvent({
        interaction_type: 'Toggle Update',
        otpMethod: newMethod,
        otpFlow: 'Settings',
      })
    } catch (error) {
      if (error instanceof CognitoUnauthorizedError) {
        if (!handleCognitoError(error, () => onToggle(mfaMethod))) {
          handleMfaError(error as MfaError, 'update')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (cognitoUser === undefined) {
    if (isCognitoUserError) {
      return (
        <ContainerStyled>
          <p>There was an error fetching your Multi-factor authentication (MFA) details.</p>
        </ContainerStyled>
      )
    } else if (cognitoUserLoading) {
      return <Spinner />
    } else {
      return null
    }
  }

  return (
    <>
      {isLoading ? <Spinner /> : null}
      <ContainerStyled data-tid-mfa-selection-screen>
        <MFASelectionItem
          iconName='LinePhoneCall01'
          mfaMethod='SMS'
          isVerified={cognitoUser.userAttributes.phone_number_verified === 'true'}
          id='mfa-phone-verification'
          isToggled={activeMethod === 'SMS'}
          contactInfo={cognitoUser.userAttributes.phone_number}
          onToggle={() => onToggle('SMS')}
          onVerify={() => onVerify('SMS')}
          onEditContact={navigateToEditContact}
        />
        <MFASelectionItem
          id='mfa-email-verification'
          iconName='LineMail02'
          mfaMethod='Email'
          isVerified={cognitoUser.userAttributes.email_verified === 'true'}
          isToggled={activeMethod === 'Email'}
          contactInfo={cognitoUser.userAttributes.email}
          onToggle={() => onToggle('Email')}
          onVerify={() => onVerify('Email')}
          onEditContact={navigateToEditContact}
        />

        {isOtpContactDetailsEditEnabled && (
          <ButtonStyled
            onClick={() => navigateToEditContact()}
            color='secondary_color'
            data-tid-edit-contact
          >
            Edit contact details
          </ButtonStyled>
        )}

        {accountNumber ? (
          <PasswordVerificationTray
            isOpen={isPasswordTrayOpen}
            onVerify={handleVerify}
            onCancel={closeVerifyPasswordTray}
            accountNumber={accountNumber}
          />
        ) : null}
      </ContainerStyled>
    </>
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',

  [lessThan('px375')]: {
    padding: spacing.smx2,
  },
})

// todo: make a secondary button component in the component lib
const ButtonStyled = styled(Button)({
  width: '75%',
  alignSelf: 'center',
  marginTop: spacing.big,
  borderRadius: radius.sm,
  background: colors.lavender[50],
  border: `1px solid ${colors.lavender[50]}`,
  color: colors.lavender[700],
  ...font.size.lg,
})
